import { formatCodeableConcept, formatObservationValue, resolveId } from '@medplum/core';
import type { Observation, Reference } from '@medplum/fhirtypes';
import { Fragment } from 'react';
import { cn } from '../../utils';
import { classifyObservation } from '../../utils/observation';
import { Badge } from '../ui/badge';
import { TableCell, TableRow } from '../ui/table';
import { formatInterpretation, formatReferenceRange, isCriticalObservation } from './observationDisplayUtils';

// Columns rendered per result row (Test, Value, Reference Range, Interpretation, Category,
// Performer, Status) — see the header in DiagnosticReportDisplay. The note sub-row spans them.
const RESULT_COLUMN_COUNT = 7;

type Props = {
  refs: Reference<Observation>[];
  observations: Map<string, Observation>;
  ancestorIds: Set<string>;
  /** When provided, each analyte row becomes clickable and calls this with the row's Observation. */
  onSelectObservation?: (observation: Observation) => void;
};

/**
 * Build the props that make a result row clickable and keyboard-operable. Returns `{}` when no
 * select handler is given, so the row stays a plain table row. The row keeps its implicit
 * `role="row"` (we intentionally do NOT override it to `button`, which would drop the row — and its
 * cells — out of the table's accessibility tree); it remains focusable and Enter/Space-operable.
 * `aria-label` falls back to the raw code, then to undefined (the row's cell text becomes the
 * accessible name) so the row is never unlabeled.
 */
function selectableRowProps(
  observation: Observation,
  onSelectObservation?: (observation: Observation) => void,
): React.ComponentProps<typeof TableRow> & { 'data-testid'?: string } {
  if (!onSelectObservation) return {};
  const ariaLabel = formatCodeableConcept(observation.code) || observation.code?.coding?.find((c) => c.code)?.code;
  return {
    className: 'cursor-pointer hover:bg-accent',
    onClick: () => onSelectObservation(observation),
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelectObservation(observation);
      }
    },
    role: 'row',
    tabIndex: 0,
    'aria-label': ariaLabel || undefined,
    'data-testid': 'diagnostic-report-result-row',
  };
}

function renderObservationRow(
  observation: Observation,
  onSelectObservation?: (observation: Observation) => void,
): React.ReactElement {
  const critical = isCriticalObservation(observation);
  const value = formatObservationValue(observation);
  const range = formatReferenceRange(observation.referenceRange);
  const interpretation = formatInterpretation(observation.interpretation);
  const category = observation.category?.[0] ? formatCodeableConcept(observation.category[0]) : null;
  const performer = (observation.performer ?? [])
    .map((p) => p.display)
    .filter((d): d is string => Boolean(d))
    .join(', ');
  const notes = (observation.note ?? [])
    .map((n) => n.text)
    .filter((text): text is string => Boolean(text))
    .join('\n');
  const rowProps = selectableRowProps(observation, onSelectObservation);

  return (
    <>
      <TableRow
        {...rowProps}
        className={cn(critical && 'bg-destructive/10', notes && 'border-b-0', rowProps.className)}
      >
        <TableCell className="font-medium">{formatCodeableConcept(observation.code) || ''}</TableCell>
        <TableCell>{value || ''}</TableCell>
        <TableCell>{range ?? ''}</TableCell>
        <TableCell>{interpretation ?? ''}</TableCell>
        <TableCell>{category ?? ''}</TableCell>
        <TableCell>{performer}</TableCell>
        <TableCell>{observation.status && <Badge variant="outline">{observation.status}</Badge>}</TableCell>
      </TableRow>
      {notes && (
        // Carry the row's click/keyboard affordances onto the note sub-row so clicking the note
        // (visually part of the result) selects the same observation. Drop the data-testid so tests
        // still pinpoint the main data row.
        <TableRow
          {...rowProps}
          data-testid={undefined}
          className={cn(critical && 'bg-destructive/10', rowProps.className)}
        >
          <TableCell colSpan={RESULT_COLUMN_COUNT} className="pt-0">
            <div className="border-l-4 border-blue-500 bg-blue-50 px-3 py-2 text-sm whitespace-pre-wrap dark:border-blue-400 dark:bg-blue-950/40">
              {notes}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/**
 * Render a list of observation references as table rows. Walks `hasMember` recursively for
 * panel-style results (e.g. CBC w/ Diff → WBC, RBC, Hgb, ...). `ancestorIds` short-circuits
 * cycles — an observation that references itself directly or transitively would otherwise
 * recurse forever.
 *
 * Only `result` leaves get a row. Health Gorilla nests each real result under a value-less
 * `hg-observaton-group` wrapper and emits a `document` leaf for the lab's PDF (rendered
 * separately via the report's `presentedForm`); both are skipped here — see
 * `classifyObservation` and `docs/design-decisions/2026-06-22-modeling-lab-orders-and-results.md`. We
 * still recurse through every node so wrappers' result members are reached.
 *
 * Refs whose target isn't in `observations` are silently skipped: the orchestrator may still
 * be loading them, and the loading state is handled at the section level.
 */
export const DiagnosticReportObservationRows = ({
  refs,
  observations,
  ancestorIds,
  onSelectObservation,
}: Props): React.ReactElement => (
  <>
    {refs.map((ref) => {
      const id = resolveId(ref);
      if (!id || ancestorIds.has(id)) return null;
      const obs = observations.get(id);
      if (!obs) return null;
      const memberRefs = (obs.hasMember as Reference<Observation>[] | undefined) ?? [];
      const nextAncestors = obs.id ? new Set([...ancestorIds, obs.id]) : ancestorIds;
      return (
        <Fragment key={id}>
          {classifyObservation(obs) === 'result' && renderObservationRow(obs, onSelectObservation)}
          {memberRefs.length > 0 && (
            <DiagnosticReportObservationRows
              refs={memberRefs}
              observations={observations}
              ancestorIds={nextAncestors}
              onSelectObservation={onSelectObservation}
            />
          )}
        </Fragment>
      );
    })}
  </>
);
